import React from 'react';
import { DropdownOption } from 'v3/Components/Form/Dropdown';

export type ClientSearchParams = { client?: string[] };

const readClientFromUrl = (): DropdownOption | undefined => {
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get('clientId') || undefined;
  if (!clientId) return undefined;
  return {
    id: clientId,
    value: clientId,
    displayName: params.get('clientName') ?? undefined,
  };
};

/**
 * Shared wiring between <ClientSelector /> and a <DataGrid />'s additionalSearchParams.
 *
 * Two things here are load-bearing for STRAD2-2628, where the grid fired an
 * unfiltered request before the client filter arrived:
 *
 *  1. The initial state is seeded synchronously from the URL's clientId, so the
 *     grid's very first fetch already carries the filter. ClientSelector can only
 *     report the selection after its own /clients/dropdownItems call resolves,
 *     which is a render too late.
 *  2. The params object is memoized on the primitive id, not on the option object.
 *     When ClientSelector later calls back with the same client, the memo returns
 *     the same reference, so additionalSearchParams keeps its identity and the
 *     grid's fetch effect does not re-run.
 */
export default function useClientSearchParams() {
  const [selectedClient, setSelectedClient] = React.useState<
    DropdownOption | undefined
  >(readClientFromUrl);

  const clientId = selectedClient?.value
    ? String(selectedClient.value)
    : undefined;

  const clientSearchParams = React.useMemo<ClientSearchParams>(
    () => (clientId ? { client: [clientId] } : {}),
    [clientId]
  );

  return { selectedClient, setSelectedClient, clientSearchParams };
}
